import { useCallback, useEffect, useRef, useState } from 'react';

export const SAFE_BACK_EVENT = 'openruns:safe-back';

/**
 * useUnsavedChangesGuard — prevents accidental data loss on dirty forms.
 *
 * Works WITHOUT React Router's data-router APIs (no useBlocker), so it's safe
 * with <BrowserRouter>. It guards two leave paths:
 *
 *   1. Browser / hardware back + iOS edge-swipe-back.
 *      In the Capacitor remote-URL setup, an edge swipe triggers WKWebView
 *      goBack() which fires `popstate`. We push a one-off history "sentinel"
 *      while armed so the first back press is absorbed (the user stays put)
 *      and we can show a "Discard changes?" prompt instead.
 *
 *   2. In-app navigation buttons (Back button, links). Wrap their handler with
 *      guardedLeave(action) — it prompts when dirty, otherwise runs immediately.
 *
 * @param {Object}   opts
 * @param {boolean}  opts.active        Arm the back-trap (form dirty, or modal open).
 * @param {boolean}  opts.shouldPrompt  Show the discard dialog when leaving.
 * @param {Function} [opts.onLeave]     Leave action for the back-trap path.
 *                                       Default: perform the real browser back.
 *                                       For a modal, pass a close-modal fn (stays on page).
 * @returns {{ promptOpen:boolean, confirmLeave:Function, cancelLeave:Function, guardedLeave:Function }}
 */
export function useUnsavedChangesGuard({ active, shouldPrompt, onLeave }) {
  const [promptOpen, setPromptOpen] = useState(false);
  const pendingActionRef = useRef(null);
  const shouldPromptRef = useRef(shouldPrompt);
  const onLeaveRef = useRef(onLeave);

  useEffect(() => { shouldPromptRef.current = shouldPrompt; }, [shouldPrompt]);
  useEffect(() => { onLeaveRef.current = onLeave; }, [onLeave]);

  useEffect(() => {
    if (!active) return;

    let leaving = false;

    // Sentinel: a back press pops this first, keeping us on the current page.
    window.history.pushState({ __ucGuard: true }, '');

    const realBack = () => {
      leaving = true;
      window.removeEventListener('popstate', onPop);
      // Pop the (re-pushed) sentinel + the page entry to reach the previous page.
      window.history.go(-2);
    };

    const onPop = () => {
      if (leaving) return;
      // Re-arm the sentinel so we stay on the page while the user decides.
      window.history.pushState({ __ucGuard: true }, '');
      const leaveAction = onLeaveRef.current || realBack;
      if (shouldPromptRef.current) {
        pendingActionRef.current = leaveAction;
        setPromptOpen(true);
      } else {
        leaveAction();
      }
    };

    window.addEventListener('popstate', onPop);

    return () => {
      window.removeEventListener('popstate', onPop);
      // Drop our sentinel if it's still the top entry (invisible, same URL).
      if (!leaving && window.history.state && window.history.state.__ucGuard) {
        window.history.back();
      }
    };
  }, [active]);

  useEffect(() => {
    if (!active) return undefined;

    const onSafeBack = (event) => {
      const leaveAction = event.detail?.action;
      if (typeof leaveAction !== 'function') {
        return;
      }

      if (shouldPromptRef.current) {
        event.preventDefault();
        pendingActionRef.current = leaveAction;
        setPromptOpen(true);
      }
    };

    window.addEventListener(SAFE_BACK_EVENT, onSafeBack);

    return () => {
      window.removeEventListener(SAFE_BACK_EVENT, onSafeBack);
    };
  }, [active]);

  // For in-app buttons / links.
  const guardedLeave = useCallback((action) => {
    if (shouldPromptRef.current) {
      pendingActionRef.current = action;
      setPromptOpen(true);
    } else {
      action();
    }
  }, []);

  const confirmLeave = useCallback(() => {
    setPromptOpen(false);
    const fn = pendingActionRef.current;
    pendingActionRef.current = null;
    if (fn) fn();
  }, []);

  const cancelLeave = useCallback(() => {
    setPromptOpen(false);
    pendingActionRef.current = null;
  }, []);

  return { promptOpen, confirmLeave, cancelLeave, guardedLeave };
}
